import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PathToLiveStage } from '../components/service-definition-details/service-definition-details.component';

export type PortalView = 'home' | 'blueprint' | 'environment' | 'service-details' | 'faq';

export interface PortalState {
  currentView: PortalView;
  serviceId?: string;
  serviceName?: string;
  blueprintId?: string;
  envId?: string;
  envName?: string;
  envStage?: EnvironmentStage;
}

export interface EnvironmentStage {
  envId: string;
  environment: string;
  deploymentClass: string;
  version?: string;
  releasePackage?: string;
  blueprintName?: string;  
  status: 'HEALTHY' | 'IN_PROGRESS';
  hasInstance?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PortalStateService {
  envStage?: EnvironmentStage; 

  private readonly DEFAULT_STATE: PortalState = {
    currentView: 'home'
  };

  private state$ = new BehaviorSubject<PortalState>(this.DEFAULT_STATE);

  getCurrentState(): Observable<PortalState> {
    return this.state$.asObservable();
  }

  getCurrentStateSnapshot(): PortalState {
    return this.state$.value;
  }

  goHome(): void {
    this.state$.next({
      currentView: 'home'
    });
  }

  viewFaq(): void {
    this.state$.next({
      currentView: 'faq'
    });
  }

  viewServiceDetails(serviceId: string, serviceName?: string, blueprintId?: string): void {
    this.state$.next({
      currentView: 'service-details',
      serviceId,
      serviceName,
      blueprintId,
    });
  }

  /** Updates the display name of the selected service, e.g. once its blueprint name has loaded. */
  setServiceName(serviceId: string, serviceName: string): void {
    const state = this.state$.value;
    if (state.serviceId !== serviceId) {
      return;
    }
    this.state$.next({ ...state, serviceName });
  }

  viewBlueprint(serviceId: string, blueprintId: string): void {
    this.state$.next({
      currentView: 'blueprint',
      serviceId,
      blueprintId
    });
  }

  /** Opens an environment of the currently selected service definition. */
  viewEnvironment(envId: string, envName: string, stage?: PathToLiveStage, envStage?: EnvironmentStage): void {
    const { serviceId, serviceName, blueprintId  } = this.state$.value;
    this.state$.next({
      currentView: 'environment',
      serviceId,
      serviceName,
      envId,
      envName,
      envStage,
      blueprintId,  
    });
  }

}
